import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import {
  createInstitutionTaxSelfCertificationDefaults,
  type InstitutionTaxSelfCertification,
  institutionDetailsTaxSelfCertificationApiToInstitutionTaxSelfCertification,
  institutionTaxSelfCertificationClassificationSchema,
  institutionTaxSelfCertificationControllingPersonsSchema,
  institutionTaxSelfCertificationDeclarationSchema,
  institutionTaxSelfCertificationResidencySchema,
  institutionTaxSelfCertificationToInstitutionDetailsTaxSelfCertificationApi,
} from '@/features/institution/tax-self-certification/schema';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionTaxSelfCertificationFieldsetClassification, InstitutionTaxSelfCertificationFieldsetClassificationLegend } from './fieldsets/classification';
import { InstitutionTaxSelfCertificationFieldsetControllingPersons, InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend } from './fieldsets/controlling-persons';
import { InstitutionTaxSelfCertificationFieldsetDeclaration, InstitutionTaxSelfCertificationFieldsetDeclarationLegend } from './fieldsets/declaration';
import { InstitutionTaxSelfCertificationFieldsetResidency, InstitutionTaxSelfCertificationFieldsetResidencyLegend } from './fieldsets/residency';

const FORM_STEPS = {
  RESIDENCY: {
    Component: InstitutionTaxSelfCertificationFieldsetResidency,
    Legend: InstitutionTaxSelfCertificationFieldsetResidencyLegend,
    schema: institutionTaxSelfCertificationResidencySchema,
  },
  CLASSIFICATION: {
    Component: InstitutionTaxSelfCertificationFieldsetClassification,
    Legend: InstitutionTaxSelfCertificationFieldsetClassificationLegend,
    schema: institutionTaxSelfCertificationClassificationSchema,
  },
  CONTROLLING_PERSONS: {
    Component: InstitutionTaxSelfCertificationFieldsetControllingPersons,
    Legend: InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend,
    schema: institutionTaxSelfCertificationControllingPersonsSchema,
  },
  DECLARATION: {
    Component: InstitutionTaxSelfCertificationFieldsetDeclaration,
    Legend: InstitutionTaxSelfCertificationFieldsetDeclarationLegend,
    schema: institutionTaxSelfCertificationDeclarationSchema,
  },
} satisfies Record<string, FormStep<InstitutionTaxSelfCertification>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionTaxSelfCertificationFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionTaxSelfCertification>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'taxSelfCertification', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionTaxSelfCertificationFormCurrentStep.displayName = 'InstitutionTaxSelfCertificationFormCurrentStep';

const InstitutionTaxSelfCertificationFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionTaxSelfCertification>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionTaxSelfCertificationFormCurrentStepLegend.displayName = 'InstitutionTaxSelfCertificationFormCurrentStepLegend';

const InstitutionTaxSelfCertificationFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionTaxSelfCertification>();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('taxSelfCertification');
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(prevDetailsRoute, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionTaxSelfCertificationFormBackButton.displayName = 'InstitutionTaxSelfCertificationFormBackButton';

const InstitutionTaxSelfCertificationFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionTaxSelfCertification>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionTaxSelfCertificationFormFooter.displayName = 'InstitutionTaxSelfCertificationFormFooter';

const InstitutionTaxSelfCertificationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'taxSelfCertification');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('taxSelfCertification');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionTaxSelfCertification, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { taxSelfCertification: await institutionTaxSelfCertificationToInstitutionDetailsTaxSelfCertificationApi(data, new Date()) };

    await mutateAsync(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
      },
    });
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.taxSelfCertification
    ? institutionDetailsTaxSelfCertificationApiToInstitutionTaxSelfCertification(institutionDetailsAnswers.taxSelfCertification)
    : createInstitutionTaxSelfCertificationDefaults(institutionDetailsAnswers);

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionTaxSelfCertificationFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionTaxSelfCertificationFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionTaxSelfCertification) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionTaxSelfCertificationFormCurrentStep />
          <InstitutionTaxSelfCertificationFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionTaxSelfCertificationForm.displayName = 'InstitutionTaxSelfCertificationForm';

export default InstitutionTaxSelfCertificationForm;
